import { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, Users, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLiteMode } from "../contexts/LiteModeContext";
import PageHeader from "../components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import TeamAccessPage from "./TeamAccessPage";

/**
 * Mode allégé — page « Configuration » (/admin/configuration).
 * Onglet 1 : fonctions activées au général (modules de l'organisation).
 * Onglet 2 : fonctions par utilisateur (matrice de droits existante).
 */
export default function LiteConfigPage() {
  const { axiosAuth, checkAuth } = useAuth();
  const { lite, ready } = useLiteMode();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ready) return;
    api.get("/lite/config")
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null))
      .finally(() => setLoading(false));
  }, [api, ready]);

  const grouped = useMemo(() => {
    const g = {};
    (config?.modules || []).forEach((m) => {
      if (!g[m.category]) g[m.category] = [];
      g[m.category].push(m);
    });
    return g;
  }, [config]);

  const toggle = async (key) => {
    if (saving || !config?.can_edit) return;
    const next = config.modules.map((m) => (m.key === key ? { ...m, active: !m.active } : m));
    setConfig({ ...config, modules: next });
    setSaving(true);
    try {
      await api.put("/lite/config", { active_modules: next.filter((m) => m.active).map((m) => m.key) });
      toast.success("Configuration enregistrée");
      checkAuth?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
      setConfig(config);
    } finally {
      setSaving(false);
    }
  };

  if (!ready || loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-400 text-sm" data-testid="lite-config-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  if (!lite || !config) {
    return (
      <div className="p-6" data-testid="lite-config-off">
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 max-w-xl flex items-start gap-3">
          <Info className="w-5 h-5 text-[#f7c935] flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-sm font-bold text-white">Mode allégé non activé</h2>
            <p className="text-xs text-slate-400 mt-1">
              Cette page est réservée aux instances déployées en mode allégé. Ajoutez
              <code className="text-[#f7c935] px-1">LITE_MODE=true</code> dans la configuration du backend pour l'activer.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="lite-config-page">
      <PageHeader
        icon={<SlidersHorizontal />}
        title="Configuration"
        description={`Choisissez les fonctions activées pour ${config.tenant_name || "votre organisation"} — au général et par utilisateur.`}
        testId="lite-config-header"
      />

      <Tabs defaultValue="general">
        <TabsList className="bg-[#14101c] border border-white/5">
          <TabsTrigger value="general" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="lite-tab-general">
            <SlidersHorizontal className="w-4 h-4" /> Fonctions générales
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="lite-tab-users">
            <Users className="w-4 h-4" /> Par utilisateur
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          {!config.can_edit && (
            <p className="text-xs text-amber-300 mb-3" data-testid="lite-config-readonly">
              Lecture seule — seuls les administrateurs peuvent modifier la configuration.
            </p>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-6xl">
            {Object.entries(grouped).map(([cat, mods]) => (
              <div key={cat} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={`lite-cat-${cat.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <h3 className="text-[11px] font-black text-[#f7c935] uppercase tracking-wider mb-3">{cat}</h3>
                <div className="space-y-2">
                  {mods.map((m) => (
                    <label
                      key={m.key}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-[#0b0811] border border-white/5 cursor-pointer hover:border-[#f7c935]/30 transition-colors"
                    >
                      <span className={`text-xs ${m.active ? "text-white" : "text-slate-500"}`}>{m.label}</span>
                      <Switch
                        checked={m.active}
                        disabled={!config.can_edit || saving}
                        onCheckedChange={() => toggle(m.key)}
                        data-testid={`lite-module-${m.key}`}
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-4">
            Les changements s'appliquent immédiatement : la barre latérale de chaque utilisateur reflète les fonctions actives à sa prochaine navigation.
          </p>
        </TabsContent>

        <TabsContent value="users" className="mt-2">
          <TeamAccessPage embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
